import { getDataApiUrl, API_CONFIG } from '../config/api';
import authService from './authService';

export interface Status {
  id: number;
  name: string;
  description?: string;
  color: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStatusRequest {
  name: string;
  description?: string;
  color?: string;
}

export interface UpdateStatusRequest {
  name?: string;
  description?: string;
  color?: string;
}

export interface StatusSearchRequest {
  name?: string;
  isActive?: boolean;
  createdAtFrom?: string;
  createdAtTo?: string;
}

export interface PaginatedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
}

class StatusService {
  private baseUrl = '/api/statuses';

  private getAuthHeaders(): HeadersInit {
    const token = authService.getAccessToken();
    return {
      ...API_CONFIG.HEADERS,
      'Authorization': `Bearer ${token}`
    };
  }

  // Получение всех статусов с пагинацией
  async getStatuses(page = 0, size = 20, sortBy = 'id', sortDir = 'ASC'): Promise<PaginatedResponse<Status>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
      sortBy,
      sortDir
    });
    
    const url = getDataApiUrl(`${this.baseUrl}?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение только активных статусов
  async getActiveStatuses(): Promise<Status[]> {
    const url = getDataApiUrl(`${this.baseUrl}/active`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение статуса по ID
  async getStatusById(id: number): Promise<Status> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Создание нового статуса
  async createStatus(statusData: CreateStatusRequest): Promise<Status> {
    const url = getDataApiUrl(this.baseUrl);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(statusData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Обновление статуса
  async updateStatus(id: number, statusData: UpdateStatusRequest): Promise<Status> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(statusData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Удаление статуса
  async deleteStatus(id: number): Promise<void> {
    const url = getDataApiUrl(`${this.baseUrl}/${id}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  // Поиск статусов
  async searchStatuses(searchData: StatusSearchRequest, page = 0, size = 20): Promise<PaginatedResponse<Status>> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });
    
    const url = getDataApiUrl(`${this.baseUrl}/search?${params}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(searchData)
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение статуса по названию
  async getStatusByName(name: string): Promise<Status> {
    const url = getDataApiUrl(`${this.baseUrl}/name/${encodeURIComponent(name)}`);
    const headers = this.getAuthHeaders();
    
    const response = await fetch(url, {
      method: 'GET',
      headers
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return response.json();
  }

  // Получение стандартных статусов для заявок
  getDefaultRequestStatuses(): CreateStatusRequest[] {
    return [
      {
        name: 'Новая',
        description: 'Заявка создана и ожидает обработки',
        color: '#3B82F6'
      },
      {
        name: 'В работе',
        description: 'Заявка принята в работу',
        color: '#F59E0B'
      },
      {
        name: 'Завершено',
        description: 'Заявка выполнена',
        color: '#10B981'
      },
      {
        name: 'Отменено',
        description: 'Заявка отменена',
        color: '#EF4444'
      }
    ];
  }

  // Получение стандартных статусов для рейсов
  getDefaultFlightStatuses(): CreateStatusRequest[] {
    return [
      {
        name: 'Запланирован',
        description: 'Рейс запланирован',
        color: '#3B82F6'
      },
      {
        name: 'Выполняется',
        description: 'Рейс в процессе выполнения',
        color: '#F59E0B'
      },
      {
        name: 'Завершен',
        description: 'Рейс завершен',
        color: '#10B981'
      },
      {
        name: 'Отменен',
        description: 'Рейс отменен',
        color: '#EF4444'
      },
      {
        name: 'Задержан',
        description: 'Рейс задержан',
        color: '#8B5CF6'
      }
    ];
  }
}

const statusService = new StatusService();
export default statusService; 